import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from "@angular/common/http";
import { UrlService } from "../../../../../../../environments/urlService";
import { MetricDataRole } from "../../../../dto/MetricData";
import { UUID } from "../../../../../../core/pojo/id-variable";
import { LocalDate } from "../../../../../../core/pojo/date-variable";
import { MetricDataProperty } from "../../../../../recovery/component/recovery-metrics-panel/entity/DefaultMetric";
import { Observable } from 'rxjs';
import { IGNORE_ERROR_HANDLING, IS_SPINNER_ACTIVE } from "../../../../../../util/interceptors";

@Injectable({
  providedIn: 'root',
})
export class KpiCrossCompanyService {
  private readonly url: string;

  constructor(
    private http: HttpClient,
    private urlService: UrlService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'kpi/metrics/cross-company';
  }

  /**
   * Remember list of companies and metrics for future cross company requests.
   * Next time {@link getAnalysis} is called it'll return provided here {@link analysisDto}.
   *
   * @param analysisDto contains list of companies and metrics to save with org uuid as a key
   */
  saveAnalysis(analysisDto: CrossCompanyAnalysisDto): Observable<void> {
    return this.http.post<void>(this.url + '/analysis', analysisDto, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  /**
   * Get last saved request for cross company.
   *
   * @param orgUUID by which the request is saved
   */
  getAnalysis(orgUUID: UUID): Observable<CrossCompanyAnalysisDto> {
    return this.http.get<CrossCompanyAnalysisDto>(this.url + '/analysis', {
      params: { orgUUID },
      withCredentials: true,
      context: new HttpContext()
        .set(IS_SPINNER_ACTIVE, true)
        .set(IGNORE_ERROR_HANDLING, true), // org does not have cross company wrapper by default.,
    });
  }

  /**
   * Get cross company data for provided org uuid
   *
   * @param orgUUID by which the request is saved
   * @returns {@link CrossCompanyResponse}
   */
  getData(orgUUID: UUID): Observable<CrossCompanyResponse> {
    return this.http.get<CrossCompanyResponse>(this.url, {
      params: { orgUUID },
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }
}

export class CrossCompanyAnalysisDto {
  orgUUID: UUID;
  companies: CrossCompanyAnalysisCompaniesDTO[];
  metrics: CrossCompanyAnalysisMetricsDTO[];
}

export class CrossCompanyAnalysisCompaniesDTO {
  companyUUID: UUID;
}

export class CrossCompanyAnalysisMetricsDTO {
  metricUUID: UUID;
  role: MetricDataRole;
}

export class CrossCompanyResponse {
  periods: LocalDate[];
  metrics: MetricCrossCompanyDTO[];
  isEmptyDataProvided: boolean = false;
}

export class MetricCrossCompanyDTO {
  name: string;
  groupName: string;
  role: MetricDataRole;
  dataProps: MetricDataProperty;
  companies: CompanyCrossCompanyDTO[];
  showCompanies: boolean = true;
}

export class CompanyCrossCompanyDTO {
  companyUUID: UUID;
  companyName: string;
  data: MetricDataCrossCompanyDTO[];
}

export class MetricDataCrossCompanyDTO {
  uuid: UUID;
  rowValue: string;
  period: LocalDate;
}
